import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import SoftButton from 'components/SoftButton';
import Grid from '@mui/material/Grid';
import SoftTypography from 'components/SoftTypography';
import FormField from 'layouts/applications/wizard/components/FormField';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

import TransportSelector from 'components/TransportSelector';
import TrafficStatesSelector from 'components/TrafficStatesSelector';

const ModalTrafficDetails = ({ show, handleClose, initialData }) => {
  const { numeroOrdenDetalle } = useParams();
  const [formData, setFormData] = useState({
    nombre: '',
    navieraLinea: '',
    desde: '',
    hasta: '',
    fechasalida: '',
    fechaestimadaarribo: '',
    transporte: '',
    estado: ''
  });

  // Si existe initialData (modo edición), se llama al endpoint para obtener el registro
  useEffect(() => {
    const editingData = Array.isArray(initialData) ? initialData[0] : initialData;
    if (editingData && (editingData.id_trafico_detalle || editingData.id)) {
      const idToFetch = editingData.id_trafico_detalle || editingData.id;
      axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/orders/traffic-details-info/`, {
          params: { id: idToFetch }
        })
        .then(response => {
          const apiData = Array.isArray(response.data)
            ? response.data[0]
            : response.data;
          console.log("Datos obtenidos para edición detalle:", apiData);
          setFormData({
            nombre: apiData.nombre || '',
            navieraLinea: apiData.nav_linea || '',
            desde: apiData.desde || '',
            hasta: apiData.hasta || '',
            fechasalida: apiData.f1 || '',
            fechaestimadaarribo: apiData.f2 || '',
            // Si el endpoint no retorna estos campos, se mantienen en vacío o se ajusta según sea necesario:
            transporte: apiData.id_tipo_transporte || '',
            estado: apiData.id_estado_trafico || ''
          });
        })
        .catch(error => {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error al obtener los datos del detalle de tráfico.'
          });
          console.error("Error fetching traffic details info:", error);
        });
    } else {
      // Si no está en modo edición, se limpian los inputs
      setFormData({
        nombre: '',
        navieraLinea: '',
        desde: '',
        hasta: '',
        fechasalida: '',
        fechaestimadaarribo: '',
        transporte: '',
        estado: ''
      });
    }
  }, [initialData, show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field, value) => {
    console.log(`Actualizando ${field}:`, value);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const editingData = Array.isArray(initialData) ? initialData[0] : initialData;
  const isEditing = editingData && (editingData.id_trafico_detalle || editingData.id);

  const handleSave = () => {
    if (!formData.nombre || !formData.navieraLinea) {
      Swal.fire({
        icon: 'error',
        title: 'Campos incompletos',
        text: 'Debe completar los campos de Nombre y Naviera o Línea Aérea.'
      });
      return;
    }

    // Se arma el objeto con los nombres que espera el backend
    const postData = {
      id: isEditing ? (editingData.id_trafico_detalle || editingData.id) : numeroOrdenDetalle,
      nom: formData.nombre,
      n_l: formData.navieraLinea,
      des: formData.desde,
      has: formData.hasta,
      f1: formData.fechasalida,
      f2: formData.fechaestimadaarribo,
      t_tra: formData.transporte,
      e_tra: typeof formData.estado === 'object' ? formData.estado.value : formData.estado
    };

    if (isEditing) {
      // Actualización: se usa PATCH para actualizar el registro
      axios
        .patch(`${process.env.REACT_APP_SERVER_URL}/api/orders/traffic-details/`, postData)
        .then(response => {
          Swal.fire({
            icon: 'success',
            title: 'Actualizado',
            text: 'El detalle de tráfico ha sido actualizado con éxito!'
          });
          handleClose();
        })
        .catch(error => {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error actualizando el detalle de tráfico. Inténtelo nuevamente.'
          });
          console.error("Error actualizando tráfico detalle:", error);
        });
    } else {
      // Creación de un nuevo registro
      axios
        .post(`${process.env.REACT_APP_SERVER_URL}/api/orders/traffic-details/`, postData)
        .then(response => {
          Swal.fire({
            icon: 'success',
            title: 'Guardado',
            text: 'El detalle de tráfico ha sido ingresado con éxito!'
          });
          handleClose();
        })
        .catch(error => {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error guardando el detalle de tráfico. Debe existir una cabecera.'
          });
          console.error("Debe existir una cabecera:", error);
        });
    }
  };

  return (
    <Modal
      aria-labelledby="contained-modal-title-vcenter"
      backdropClassName="modal-backdrop"
      className="modal-container"
      style={{ zIndex: 1050 }}
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Editar Detalle Trafico' : 'Agregar Detalle Trafico'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Grid container spacing={2}>
          {/* Columna 1 */}
          <Grid item xs={12} md={6}>
            <SoftTypography variant="caption" fontWeight="bold" color="text">
              Nombre transporte
            </SoftTypography>
            <FormField
              type="text"
              placeholder="Ingrese el nombre transporte"
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
            />

            <SoftTypography variant="caption" fontWeight="bold" color="text">
              Trafico desde
            </SoftTypography>
            <FormField
              type="text"
              placeholder="Ingrese tráfico desde"
              name="desde"
              value={formData.desde}
              onChange={handleInputChange}
            />

            <SoftTypography variant="caption" fontWeight="bold" color="text">
              Fecha salida
            </SoftTypography>
            <FormField
              type="date"
              placeholder="Seleccione fecha salida"
              name="fechasalida"
              value={formData.fechasalida}
              onChange={handleInputChange}
            />

            <TransportSelector
              selectedTransport={formData.transporte}
              setSelectedTransport={(value) => handleSelectChange('transporte', value)}
            />
          </Grid>
          {/* Columna 2 */}
          <Grid item xs={12} md={6}>
            <SoftTypography variant="caption" fontWeight="bold" color="text">
              Naviera o línea aérea
            </SoftTypography>
            <FormField
              type="text"
              placeholder="Ingrese naviera o línea aérea"
              name="navieraLinea"
              value={formData.navieraLinea}
              onChange={handleInputChange}
            />

            <SoftTypography variant="caption" fontWeight="bold" color="text">
              Trafico hasta
            </SoftTypography>
            <FormField
              type="text"
              placeholder="Ingrese tráfico hasta"
              name="hasta"
              value={formData.hasta}
              onChange={handleInputChange}
            />

            <SoftTypography variant="caption" fontWeight="bold" color="text">
              Fecha estimada arribo
            </SoftTypography>
            <FormField
              type="date"
              placeholder="Seleccione fecha estimada arribo"
              name="fechaestimadaarribo"
              value={formData.fechaestimadaarribo}
              onChange={handleInputChange}
            />

            <TrafficStatesSelector
              selectedTrafficStates={formData.estado}
              setSelectedTrafficStates={(value) => handleSelectChange('estado', value)}
            />
          </Grid>
        </Grid>
      </Modal.Body>
      <Modal.Footer>
        <SoftButton variant="gradient" color="success" onClick={handleSave}>
          Guardar
        </SoftButton>
        <SoftButton variant="gradient" color="error" onClick={handleClose}>
          Cancelar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
};

ModalTrafficDetails.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialData: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
};

export default ModalTrafficDetails;
